import React, { useCallback, useMemo, useRef } from 'react';
import styles from '../../styles/contacts/ContactsWorkspace.module.css';
import ContactAvatar from './ContactAvatar';
import ContactMenu from './ContactMenu';
import { FlagIcon, MoreIcon } from './ContactsIcons';
import {
  SOURCE_META,
  contactAddedAt,
  contactDisplayName,
  contactSource,
  formatFullDate,
  formatRelativeDate,
} from './contactsData';
import { useLongPress } from '../../hooks/useLongPress';

/**
 * Explicit label for the row/card hit area. The visible content is name,
 * badges and a role/company line; read out verbatim that becomes
 * "Ana SilvaManualPartner · Northbank", so the button states it plainly.
 */
function hitAreaLabel(contact) {
  return [
    contactDisplayName(contact),
    contact.role,
    contact.company,
    contact.contactDetailsSent ? 'card sent' : contact.theyHaveMyCard ? 'has your card' : null,
  ]
    .filter(Boolean)
    .join(', ');
}

function Badges({ contact }) {
  const source = contactSource(contact);
  return (
    <>
      {source !== 'tap' ? (
        <span className={styles.badge} title={SOURCE_META[source].full}>
          {SOURCE_META[source].label}
        </span>
      ) : null}
      {contact.contactDetailsSent ? (
        <span className={`${styles.badge} ${styles.badgeSent}`} title="You have sent your card to this contact">
          Card sent
        </span>
      ) : contact.theyHaveMyCard ? (
        <span className={`${styles.badge} ${styles.badgeSent}`} title="This contact has a copy of your card">
          Has your card
        </span>
      ) : null}
    </>
  );
}

function ContactRow({
  contact,
  isSelected,
  isChecked,
  isMenuOpen,
  menuVariant,
  dropUp,
  registerRef,
  onActivate,
  onToggleCheck,
  onToggleFlag,
  onOpenMenu,
  onCloseMenu,
  menuActions,
  now,
}) {
  const name = contactDisplayName(contact);
  const added = contactAddedAt(contact);
  const secondary = [contact.role, contact.company].filter(Boolean);
  const longPress = useLongPress({
    enabled: true,
    onLongPress: () => onOpenMenu(contact._id),
    onRightClick: () => onOpenMenu(contact._id),
  });

  return (
    <li
      className={[
        styles.row,
        isSelected ? styles.rowSelected : '',
        isChecked ? styles.rowChecked : '',
        styles.menuAnchor,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-current={isSelected ? 'true' : undefined}
    >
      <span className={styles.checkSlot}>
        <input
          type="checkbox"
          className={styles.check}
          checked={isChecked}
          onChange={() => onToggleCheck(contact._id)}
          aria-label={`Select ${name}`}
        />
      </span>

      <button
        type="button"
        ref={(node) => registerRef(contact._id, node)}
        className={styles.rowMain}
        onClick={() => onActivate(contact)}
        aria-label={hitAreaLabel(contact)}
        {...longPress}
      >
        <ContactAvatar contact={contact} />
        <span className={styles.rowBody}>
          <span className={styles.rowTitleLine}>
            <span className={styles.rowName}>{name}</span>
            <Badges contact={contact} />
          </span>
          <span className={styles.rowSubLine}>
            {contact.role ? <span className={styles.rowSubStrong}>{contact.role}</span> : null}
            {contact.role && contact.company ? <span className={styles.dot} aria-hidden="true" /> : null}
            {contact.company ? <span className={styles.rowSubText}>{contact.company}</span> : null}
            {secondary.length === 0 ? (
              <span className={styles.rowSubText}>{contact.email || 'No details recorded'}</span>
            ) : null}
          </span>
        </span>
      </button>

      <span className={styles.rowTools}>
        <span className={styles.rowDate} title={added ? formatFullDate(added) : undefined}>
          {formatRelativeDate(added, now)}
        </span>
        <button
          type="button"
          className={`${styles.iconBtn} ${contact.isFlagged ? styles.iconBtnOn : ''}`}
          onClick={() => onToggleFlag(contact)}
          aria-pressed={Boolean(contact.isFlagged)}
          aria-label={contact.isFlagged ? `Remove flag from ${name}` : `Flag ${name} for follow-up`}
          title={contact.isFlagged ? 'Remove flag' : 'Flag for follow-up'}
        >
          <FlagIcon filled={Boolean(contact.isFlagged)} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => (isMenuOpen ? onCloseMenu() : onOpenMenu(contact._id))}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          aria-label={`Actions for ${name}`}
        >
          <MoreIcon />
        </button>
      </span>

      {isMenuOpen ? (
        <ContactMenu
          contact={contact}
          variant={menuVariant}
          dropUp={dropUp}
          onClose={onCloseMenu}
          actions={menuActions}
        />
      ) : null}
    </li>
  );
}

function ContactCardItem({
  contact,
  isSelected,
  isChecked,
  isMenuOpen,
  menuVariant,
  dropUp,
  registerRef,
  onActivate,
  onToggleCheck,
  onToggleFlag,
  onOpenMenu,
  onCloseMenu,
  menuActions,
  now,
}) {
  const name = contactDisplayName(contact);
  const added = contactAddedAt(contact);
  const longPress = useLongPress({
    enabled: true,
    onLongPress: () => onOpenMenu(contact._id),
    onRightClick: () => onOpenMenu(contact._id),
  });

  return (
    <li
      className={[
        styles.card,
        isSelected ? styles.cardSelected : '',
        isChecked ? styles.cardChecked : '',
        styles.menuAnchor,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-current={isSelected ? 'true' : undefined}
    >
      <button
        type="button"
        ref={(node) => registerRef(contact._id, node)}
        className={styles.cardMain}
        onClick={() => onActivate(contact)}
        aria-label={hitAreaLabel(contact)}
        {...longPress}
      >
        <span className={styles.cardTop}>
          <ContactAvatar contact={contact} />
          <span className={styles.cardIdentity}>
            <span className={styles.cardName}>{name}</span>
            <span className={styles.cardRole}>{contact.role || contact.email || 'No role recorded'}</span>
          </span>
        </span>

        <span className={styles.cardBadges}>
          <Badges contact={contact} />
        </span>

        <span className={styles.cardRule} aria-hidden="true" />

        <span className={styles.cardFoot}>
          <span className={styles.cardCompany}>{contact.company || 'No company'}</span>
          <span className={styles.cardDate}>{formatRelativeDate(added, now)}</span>
        </span>
      </button>

      <span className={styles.cardTools}>
        <span className={styles.cardCheckSlot}>
          <input
            type="checkbox"
            className={styles.check}
            checked={isChecked}
            onChange={() => onToggleCheck(contact._id)}
            aria-label={`Select ${name}`}
          />
        </span>
        <button
          type="button"
          className={`${styles.iconBtn} ${contact.isFlagged ? styles.iconBtnOn : ''}`}
          onClick={() => onToggleFlag(contact)}
          aria-pressed={Boolean(contact.isFlagged)}
          aria-label={contact.isFlagged ? `Remove flag from ${name}` : `Flag ${name} for follow-up`}
        >
          <FlagIcon filled={Boolean(contact.isFlagged)} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => (isMenuOpen ? onCloseMenu() : onOpenMenu(contact._id))}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          aria-label={`Actions for ${name}`}
        >
          <MoreIcon />
        </button>
      </span>

      {isMenuOpen ? (
        <ContactMenu
          contact={contact}
          variant={menuVariant}
          dropUp={dropUp}
          onClose={onCloseMenu}
          actions={menuActions}
        />
      ) : null}
    </li>
  );
}

export function ContactListSkeleton({ rows = 7 }) {
  return (
    <div className={styles.skeletonList} aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className={styles.skeletonRow}>
          <span className={`${styles.shimmer} ${styles.skeletonAvatar}`} />
          <span className={styles.skeletonLines}>
            <span className={styles.shimmer} style={{ height: 12, width: `${68 - index * 4}%` }} />
            <span className={styles.shimmer} style={{ height: 10, width: `${46 - index * 3}%` }} />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Grouped contact list. Owns keyboard traversal (arrows, Home/End) so the
 * whole list behaves as a single tab stop, and renders either dense rows or
 * cards from the same data.
 */
export default function ContactList({
  groups,
  view = 'rows',
  selectedId,
  checkedIds,
  openMenuId,
  menuVariant = 'popover',
  onSelect,
  onActivate,
  onToggleCheck,
  onToggleFlag,
  onOpenMenu,
  onCloseMenu,
  menuActions,
  now,
}) {
  const itemRefs = useRef(new Map());

  const registerRef = useCallback((id, node) => {
    if (node) itemRefs.current.set(id, node);
    else itemRefs.current.delete(id);
  }, []);

  const flatIds = useMemo(
    () => groups.flatMap((group) => group.items.map((item) => item?._id).filter(Boolean)),
    [groups]
  );

  // Menus on the last few contacts open upward so they do not run off the
  // bottom of the page. Position is across the whole list, not per group.
  const dropUpIds = useMemo(() => {
    if (flatIds.length <= 4) return new Set();
    return new Set(flatIds.slice(-3));
  }, [flatIds]);

  const handleKeyDown = useCallback(
    (event) => {
      const { key } = event;
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) return;
      if (flatIds.length === 0) return;
      // An open menu or sheet owns the arrow keys while focus is inside it.
      if (event.target?.closest?.('[role="menu"], [role="dialog"]')) return;
      event.preventDefault();

      const current = flatIds.indexOf(selectedId);
      let nextIndex;
      if (key === 'ArrowDown') nextIndex = current < 0 ? 0 : Math.min(current + 1, flatIds.length - 1);
      else if (key === 'ArrowUp') nextIndex = current <= 0 ? 0 : current - 1;
      else if (key === 'Home') nextIndex = 0;
      else nextIndex = flatIds.length - 1;

      const nextId = flatIds[nextIndex];
      if (!nextId) return;
      onSelect?.(nextId);
      const node = itemRefs.current.get(nextId);
      node?.focus?.({ preventScroll: false });
    },
    [flatIds, onSelect, selectedId]
  );

  const sharedProps = {
    menuVariant,
    registerRef,
    onActivate,
    onToggleCheck,
    onToggleFlag,
    onOpenMenu,
    onCloseMenu,
    menuActions,
    now,
  };

  return (
    <div onKeyDown={handleKeyDown}>
      {groups.map((group) => (
        <section key={group.key} className={styles.group} aria-label={group.label || 'Contacts'}>
          {group.label ? (
            <header className={styles.groupHeader}>
              <span className={styles.groupLabel}>{group.label}</span>
              <span className={styles.groupCount}>{group.items.length}</span>
              <span className={styles.groupRule} aria-hidden="true" />
            </header>
          ) : null}
          <ul className={view === 'cards' ? styles.cards : styles.list}>
            {group.items.map((contact) => {
              const isSelected = contact._id === selectedId;
              const isMenuOpen = contact._id === openMenuId;
              const dropUp = dropUpIds.has(contact._id);
              if (view === 'cards') {
                return (
                  <ContactCardItem
                    key={contact._id}
                    contact={contact}
                    isSelected={isSelected}
                    isChecked={checkedIds.has(contact._id)}
                    isMenuOpen={isMenuOpen}
                    dropUp={dropUp}
                    {...sharedProps}
                  />
                );
              }
              return (
                <ContactRow
                  key={contact._id}
                  contact={contact}
                  isSelected={isSelected}
                  isChecked={checkedIds.has(contact._id)}
                  isMenuOpen={isMenuOpen}
                  dropUp={dropUp}
                  {...sharedProps}
                />
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
